// components/gcc/GCCTransformationTracker.js
import React, { useState, useCallback, useMemo } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Fab,
  Tooltip,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Alert,
  CardHeader,
  Avatar,
  Divider,
  Badge
} from '@mui/material';
import { 
  PlayArrow,
  Business,  
  ExitToApp,
  Home,
  PriorityHigh,
  Warning,
  CheckCircle,
  Error,
  Close,
  Settings,
  Assessment,
  PictureAsPdf,
  Analytics,
  Timeline,
  Insights,
  RocketLaunch,
  AutoAwesome,
  Download,
  Share,
  BarChart,
  TrendingUp,
  AccountTree
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { saveAs } from 'file-saver';

import { 
  GCC_TRANSFORMATION_FRAMEWORK, 
  DEFAULT_DRY_RUN_DATA,
  COMPREHENSIVE_VALUE_LEAKAGE_FRAMEWORK 
} from '../../data/gccFramework';

import CalculationConfigurationPanel, { DEFAULT_CALCULATION_CONFIG } from '../../data/CalculationConfigurationPanel';
import CompanyContextConfiguration, { DEFAULT_COMPANY_CONTEXT } from './CompanyContextConfiguration';
import ValueLeakageAnalysisDialog from './ValueLeakageAnalysisDialog';
import { GCCPDFGenerator } from './pdfGenerator';


const GCCTransformationTracker = () => {
  const navigate = useNavigate();
  const [currentStage, setCurrentStage] = useState(0);
  const [currentPhase, setCurrentPhase] = useState(0.1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [inProgressSteps, setInProgressSteps] = useState(new Set());
  const [valueLeakageDialogOpen, setValueLeakageDialogOpen] = useState(false);
  const [currentStepData, setCurrentStepData] = useState(null);
  
  const [selectedLeakages, setSelectedLeakages] = useState({});
  const [priorities, setPriorities] = useState({});
  const [customPainPoints, setCustomPainPoints] = useState([]);

  // Configuration state
  const [calculationConfig, setCalculationConfig] = useState(DEFAULT_CALCULATION_CONFIG);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [companyContext, setCompanyContext] = useState(DEFAULT_COMPANY_CONTEXT);
  const [companyConfigDialogOpen, setCompanyConfigDialogOpen] = useState(false);

  // NEW: PDF Report state
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);

  // Enhanced value leakage framework with config
  const enhancedValueLeakageFramework = useMemo(() => {
    const baseFramework = COMPREHENSIVE_VALUE_LEAKAGE_FRAMEWORK;
    
    return {
      ...baseFramework,
      
      // Override pain point scoring with config values
      painPointScoring: {
        impactWeights: calculationConfig.impactWeights,
        recoverabilityWeights: calculationConfig.recoverabilityWeights,
        priorityWeights: calculationConfig.priorityWeights
      },
      
      // Enhanced generatePainPointSummary with config
      generatePainPointSummary: (selectedLeakages, priorities) => {
        if (!selectedLeakages || typeof selectedLeakages !== 'object') {
          return [];
        }
        
        const painPoints = [];
        
        Object.entries(selectedLeakages).forEach(([leakageKey, isSelected]) => {
          if (isSelected) {
            const [categoryId, subTypeId] = leakageKey.split('.');
            const category = baseFramework.categories[categoryId];
            
            if (!category || !category.subTypes) {
              return;
            }
            
            const subType = category.subTypes[subTypeId];
            const priority = priorities && priorities[leakageKey];
            
            if (subType) {
              // Calculate pain point score using config values
              const impactScore = calculationConfig.impactWeights[category.impact] || calculationConfig.fallbackValues.impactScore;
              const recoverabilityScore = calculationConfig.recoverabilityWeights[category.recoverability] || calculationConfig.fallbackValues.recoverabilityScore;
              const priorityScore = calculationConfig.priorityWeights[priority] || calculationConfig.fallbackValues.priorityScore;
              const painWeight = calculationConfig.painPointBaseWeights[subTypeId] || calculationConfig.fallbackValues.painWeight;
              
              const totalScore = (impactScore + recoverabilityScore + priorityScore + painWeight) / 4;
              
              painPoints.push({
                id: leakageKey,
                name: subType.name,
                category: category.name,
                impact: category.impact,
                recoverability: category.recoverability,
                priority: priority,
                painScore: Math.round(totalScore * 10) / 10,
                businessCase: subType.businessCase,
                riskMitigation: subType.riskMitigation,
                recoveryPotential: subType.recovery,
                quantifiedImpact: subType.impact,
                symptoms: subType.symptoms || [],
                quantification: subType.quantification,
                categoryWeight: calculationConfig.categoryWeights[categoryId] || 5
              });
            }
          }
        });
        
        // Add custom pain points with config fallbacks
        customPainPoints.forEach((point, index) => {
          const impactScore = calculationConfig.impactWeights[point.impact] || calculationConfig.fallbackValues.impactScore;
          const priorityScore = calculationConfig.priorityWeights[point.priority] || calculationConfig.fallbackValues.priorityScore;
          const totalScore = (impactScore + priorityScore + calculationConfig.fallbackValues.recoverabilityScore + calculationConfig.fallbackValues.painWeight) / 4;
          
          painPoints.push({
            id: `custom-${index}`,
            name: point.name,
            category: point.category || 'Custom',
            impact: point.impact,
            recoverability: 'MEDIUM',
            priority: point.priority,
            painScore: Math.round(totalScore * 10) / 10,
            businessCase: point.businessCase,
            riskMitigation: point.riskMitigation || 'Custom mitigation required',
            recoveryPotential: point.recoveryPotential || 'To be assessed',
            quantifiedImpact: point.impact,
            symptoms: [],
            isCustom: true,
            categoryWeight: 5
          });
        });
        
        return painPoints.sort((a, b) => b.painScore - a.painScore);
      }
    };
  }, [calculationConfig, customPainPoints]);

  // Calculate progression metrics
  const progressionMetrics = useMemo(() => {
    const totalSteps = Object.values(GCC_TRANSFORMATION_FRAMEWORK.stages).reduce(
      (total, stage) => total + Object.values(stage.phases).reduce(
        (phaseTotal, phase) => phaseTotal + Object.keys(phase.subSteps).length, 0
      ), 0
    );

    const completedCount = completedSteps.size;
    const completionPercentage = totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;

    return {
      totalSteps,
      completedCount,
      completionPercentage: Math.round(completionPercentage * 100) / 100,
      remainingSteps: totalSteps - completedCount
    };
  }, [completedSteps]);

  // Get current stage data
  const currentStageData = useMemo(() => {
    return GCC_TRANSFORMATION_FRAMEWORK.stages[currentStage] || {};
  }, [currentStage]);

  // Get current phase data
  const currentPhaseData = useMemo(() => {
    return currentStageData.phases?.[currentPhase] || {};
  }, [currentStageData, currentPhase]);

  // Pain points calculation
  const painPoints = useMemo(() => {
    return enhancedValueLeakageFramework.generatePainPointSummary(selectedLeakages, priorities);
  }, [selectedLeakages, priorities, enhancedValueLeakageFramework]);

  // FIXED: Progress step handler
  const progressStep = useCallback((stepId) => {
    setCompletedSteps(prev => new Set([...prev, stepId]));
    setInProgressSteps(prev => {
      const next = new Set(prev);
      next.delete(stepId);
      return next;
    });
  }, []);

  // FIXED: Start step handler - Only opens Value Leakage for specific steps
  const startStep = useCallback((stepId, stepData) => {
    setInProgressSteps(prev => new Set([...prev, stepId]));
    
    // ONLY open Value Leakage Analysis if explicitly specified
    if (stepData.valueLeakageAnalysis === true) {
      setCurrentStepData(stepData);
      setValueLeakageDialogOpen(true);
    }
    // For other steps, mark as in progress but don't open dialog
  }, []);

  // Complete value leakage analysis
  const completeValueLeakageAnalysis = useCallback(() => {
    if (currentStepData) {
      const stepKey = Object.keys(currentPhaseData.subSteps).find(
        key => currentPhaseData.subSteps[key].valueLeakageAnalysis
      );
      if (stepKey) {
        progressStep(stepKey);
      }
      setValueLeakageDialogOpen(false);
      setCurrentStepData(null);
    }
  }, [currentStepData, currentPhaseData, progressStep]);

  // Calculate stage progress
  const getStageProgress = useCallback((stageId) => {
    const stage = GCC_TRANSFORMATION_FRAMEWORK.stages[stageId];
    if (!stage) return { completed: 0, total: 0, percentage: 0 };
    
    let totalSteps = 0;
    let completedStepsCount = 0;

    Object.values(stage.phases).forEach(phase => {
      Object.keys(phase.subSteps).forEach(stepId => {
        totalSteps++;
        if (completedSteps.has(stepId)) {
          completedStepsCount++;
        }
      });
    });

    return {
      completed: completedStepsCount,
      total: totalSteps,
      percentage: totalSteps > 0 ? (completedStepsCount / totalSteps) * 100 : 0
    };
  }, [completedSteps]);

  // NEW: Generate PDF Report
  const generatePDFReport = useCallback(() => {
    const reportData = {
      company: companyContext.parentCompany,
      gccTarget: companyContext.gccCompany,
      progress: progressionMetrics,
      currentStage: currentStageData,
      painPoints: painPoints,
      calculationConfig: calculationConfig,
      timestamp: new Date().toISOString(),
      valueLeakageAnalysis: {
        selectedLeakages,
        priorities,
        framework: enhancedValueLeakageFramework
      }
    };

    // Create a simple PDF content (in real implementation, use libraries like jsPDF)
    const pdfContent = `
GCC TRANSFORMATION REPORT
========================

Company: ${reportData.company.name}
Industry: ${reportData.company.industry}
Revenue: $${reportData.company.revenue?.toLocaleString()}
Employees: ${reportData.company.employeeCount?.toLocaleString()}

GCC Target: ${reportData.gccTarget.targetSize} employees
Budget: ${reportData.gccTarget.budgetAllocation}

TRANSFORMATION PROGRESS
-----------------------
Overall Completion: ${reportData.progress.completionPercentage}%
Completed Steps: ${reportData.progress.completedCount}
Remaining Steps: ${reportData.progress.remainingSteps}
Current Stage: Stage ${currentStage} - ${currentStageData.name}

VALUE LEAKAGE ANALYSIS
----------------------
Total Pain Points Identified: ${reportData.painPoints.length}
Top 5 Pain Points:
${reportData.painPoints.slice(0, 5).map((point, index) => 
  `${index + 1}. ${point.name} (Score: ${point.painScore})`
).join('\n')}

CALCULATION CONFIGURATION
-------------------------
Impact Weights: ${JSON.stringify(reportData.calculationConfig.impactWeights)}
Priority Weights: ${JSON.stringify(reportData.calculationConfig.priorityWeights)}

Generated on: ${new Date(reportData.timestamp).toLocaleString()}
    `;

    const blob = new Blob([pdfContent], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, `GCC-Transformation-Report-${new Date().toISOString().split('T')[0]}.txt`);
    setPdfDialogOpen(false);
  }, [companyContext, progressionMetrics, currentStageData, painPoints, calculationConfig, selectedLeakages, priorities, enhancedValueLeakageFramework, currentStage]);

  // Get step status color
  const getStepStatusColor = (isCompleted, isInProgress) => {
    if (isCompleted) return 'success';
    if (isInProgress) return 'warning';
    return 'default';
  };

  // Get step status text
  const getStepStatusText = (isCompleted, isInProgress) => {
    if (isCompleted) return 'Completed';
    if (isInProgress) return 'In Progress';
    return 'Pending';
  };

  // Enhanced Main Content Component
const MainContent = () => (
  <Box sx={{ height: 'calc(100vh - 200px)', overflow: 'auto' }}>
    {/* Stage Overview Card */}
    <Card sx={{ mb: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Stage {currentStage}: {currentStageData.name}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              {currentStageData.purpose}
            </Typography>
            <Chip 
              label={currentStageData.duration} 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white', 
                mt: 1,
                fontWeight: 'bold'
              }} 
            />
          </Box>
          <Avatar sx={{ width: 60, height: 60, bgcolor: 'rgba(255,255,255,0.2)' }}>
            <RocketLaunch fontSize="large" />
          </Avatar>
        </Box>
      </CardContent>
    </Card>

    {/* Phase Navigation Tabs */}
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Phase Navigation
      </Typography>
      <Tabs 
        value={currentPhase} 
        onChange={(e, newValue) => setCurrentPhase(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        {Object.entries(currentStageData.phases || {}).map(([phaseKey, phase]) => {
          const phaseSteps = Object.keys(phase.subSteps || {});
          const completedPhaseSteps = phaseSteps.filter(step => completedSteps.has(step)).length;
          const progress = phaseSteps.length > 0 ? (completedPhaseSteps / phaseSteps.length) * 100 : 0;
          
          return (
            <Tab 
              key={phaseKey}
              value={parseFloat(phaseKey)}
              label={
                <Box sx={{ textAlign: 'left', minWidth: 120 }}>
                  <Typography variant="subtitle2" noWrap>
                    {phase.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {completedPhaseSteps}/{phaseSteps.length} steps
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={progress} 
                    sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                  />
                </Box>
              }
            />
          );
        })}
      </Tabs>
    </Paper>

    {/* Current Phase Steps */}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {Object.entries(currentPhaseData.subSteps || {}).map(([stepKey, step]) => {
        const isCompleted = completedSteps.has(stepKey);
        const isInProgress = inProgressSteps.has(stepKey);
        const canStart = !isCompleted && !isInProgress;
        
        return (
          <Card 
            key={stepKey}
            elevation={isCompleted ? 1 : 2}
            sx={{ 
              borderLeft: 6,
              borderColor: isCompleted ? 'success.main' : isInProgress ? 'warning.main' : 'primary.main',
              transition: 'all 0.3s ease',
              transform: isInProgress ? 'scale(1.02)' : 'scale(1)',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-4px)'
              }
            }}
          >
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Badge 
                      color="primary" 
                      badgeContent={stepKey} 
                      sx={{ mr: 1 }}
                    >
                      <Typography variant="h6" component="h3">
                        {step.name}
                      </Typography>
                    </Badge>
                    {step.valueLeakageAnalysis && (
                      <Chip 
                        icon={<Insights />}
                        label="Value Leakage Analysis" 
                        color="primary" 
                        size="small"
                      />
                    )}
                  </Box>
                  
                  <Typography variant="body2" paragraph sx={{ mb: 1, color: 'text.secondary' }}>
                    <strong>🚀 Trigger:</strong> {step.trigger}
                  </Typography>
                  
                  <Typography variant="body2" paragraph sx={{ mb: 1 }}>
                    <strong>🛠️ Activity:</strong> {step.activity}
                  </Typography>
                  
                  <Typography variant="body2" paragraph sx={{ mb: 1 }}>
                    <strong>📊 Output:</strong> {step.output}
                  </Typography>
                  
                  {step.risks && step.risks.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 1 }} icon={<Warning />}>
                      <Typography variant="body2" fontWeight="bold">
                        Potential Risks:
                      </Typography>
                      {step.risks.join(', ')}
                    </Alert>
                  )}
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: { md: 'flex-end' } }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                      <Chip 
                        icon={isCompleted ? <CheckCircle /> : isInProgress ? <Timeline /> : <PriorityHigh />}
                        label={getStepStatusText(isCompleted, isInProgress)}
                        color={getStepStatusColor(isCompleted, isInProgress)}
                        variant={isCompleted ? "filled" : "outlined"}
                      />
                      <Chip 
                        label={step.estimatedDuration}
                        variant="outlined"
                        size="small"
                        color="primary"
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                      {canStart && (
                        <Tooltip title={`Start ${step.name}`}>
                          <Button 
                            variant="contained"
                            size="small"
                            onClick={() => startStep(stepKey, step)}
                            fullWidth
                            startIcon={<PlayArrow />}
                            sx={{ 
                              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                              boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                            }}
                          >
                            Start Step
                          </Button>
                        </Tooltip>
                      )}
                      {isInProgress && !step.valueLeakageAnalysis && (
                        <Button 
                          variant="contained" 
                          color="success"
                          size="small"
                          onClick={() => progressStep(stepKey)}
                          fullWidth
                          startIcon={<CheckCircle />}
                        >
                          Complete
                        </Button>
                      )}
                      {isCompleted && (
                        <Button 
                          variant="outlined" 
                          color="success"
                          size="small"
                          disabled
                          fullWidth
                          startIcon={<CheckCircle />}
                        >
                          Completed
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  </Box>
);
  // Enhanced Sidebar Component
  const Sidebar = () => (
    <Paper sx={{ p: 2, height: 'calc(100vh - 200px)', overflow: 'auto', position: 'sticky', top: 20 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountTree /> Transformation Journey
        </Typography>
        <Tooltip title="Back to Dashboard">
          <IconButton 
            size="small" 
            onClick={() => navigate('/')}
          >
            <Home />
          </IconButton>
        </Tooltip>
      </Box>
      
      <Stepper orientation="vertical" nonLinear activeStep={currentStage}>
        {Object.values(GCC_TRANSFORMATION_FRAMEWORK.stages).map((stage) => {
          const stageProgress = getStageProgress(stage.id);
          const isActive = currentStage === stage.id;
          
          return (
            <Step key={stage.id} completed={stageProgress.percentage === 100}>
              <StepLabel 
                icon={
                  <Avatar sx={{ 
                    width: 32, 
                    height: 32, 
                    bgcolor: isActive ? 'primary.main' : stageProgress.percentage === 100 ? 'success.main' : 'grey.400',
                    fontSize: '0.875rem',
                    fontWeight: 'bold'
                  }}>
                    {stage.id}
                  </Avatar>
                }
                optional={
                  <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                    {stageProgress.completed}/{stageProgress.total} steps • {stageProgress.percentage.toFixed(0)}%
                  </Typography>
                }
                onClick={() => {
                  setCurrentStage(stage.id);
                  const firstPhase = Object.keys(stage.phases)[0];
                  setCurrentPhase(parseFloat(firstPhase));
                }}
                sx={{ 
                  cursor: 'pointer',
                  bgcolor: isActive ? 'action.selected' : 'transparent',
                  borderRadius: 2,
                  p: 1,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    transform: 'translateX(4px)'
                  }
                }}
              >
                <Box>
                  <Typography 
                    variant="subtitle2" 
                    color={isActive ? 'primary.main' : 'text.primary'}
                    fontWeight={isActive ? 'bold' : 'normal'}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    {stage.name}
                    {stageProgress.percentage === 100 && (
                      <CheckCircle color="success" fontSize="small" />
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {stage.duration} • {stage.purpose}
                  </Typography>
                </Box>
              </StepLabel>
              <StepContent>
                <LinearProgress 
                  variant="determinate" 
                  value={stageProgress.percentage} 
                  sx={{ 
                    height: 6, 
                    borderRadius: 3,
                    mt: 1,
                    background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)'
                  }}
                />
              </StepContent>
            </Step>
          );
        })}
      </Stepper>

      {/* Quick Stats */}
      <Divider sx={{ my: 2 }} />
      <Box sx={{ p: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          📈 Quick Stats
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Overall Progress:</Typography>
            <Typography variant="caption" fontWeight="bold">
              {progressionMetrics.completionPercentage}%
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Pain Points:</Typography>
            <Typography variant="caption" fontWeight="bold" color="error">
              {painPoints.length}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Active Steps:</Typography>
            <Typography variant="caption" fontWeight="bold" color="warning.main">
              {inProgressSteps.size}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );

  // NEW: Quick Actions Speed Dial
  const QuickActions = () => (
    <SpeedDial
      ariaLabel="Quick Actions"
      sx={{ position: 'fixed', bottom: 24, right: 24 }}
      icon={<SpeedDialIcon />}
    >
      <SpeedDialAction
        icon={<PictureAsPdf />}
        tooltipTitle="Generate PDF Report"
        onClick={() => setPdfDialogOpen(true)}
      />
      <SpeedDialAction
        icon={<Analytics />}
        tooltipTitle="View Analytics"
        onClick={() => console.log('Open Analytics')}
      />
      <SpeedDialAction
        icon={<Share />}
        tooltipTitle="Share Progress"
        onClick={() => console.log('Share Progress')}
      />
    </SpeedDial>
  );

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      {/* Enhanced Header */}
      <AppBar position="static" sx={{ background: 'linear-gradient(45deg, #2c3e50 30%, #3498db 90%)' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <AutoAwesome sx={{ mr: 2 }} />
            <Typography variant="h6" component="div" fontWeight="bold">
              GCC Transformation Excellence Platform
            </Typography>
          </Box>

          {/* Header Actions */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Company Settings">
              <Button
                startIcon={<Business />}
                onClick={() => setCompanyConfigDialogOpen(true)}
                color="inherit"
                variant="outlined"
                size="small"
              >
                Company
              </Button>
            </Tooltip>

            <Tooltip title="Configure Calculations">
              <Button
                startIcon={<Settings />}
                onClick={() => setConfigDialogOpen(true)}
                color="inherit"
                variant="outlined"
                size="small"
              >
                Calculations
              </Button>
            </Tooltip>

            <Tooltip title="Generate Report">
              <Button
                startIcon={<PictureAsPdf />}
                onClick={() => setPdfDialogOpen(true)}
                color="inherit"
                variant="outlined"
                size="small"
              >
                Report
              </Button>
            </Tooltip>

            <Button
              startIcon={<ExitToApp />}
              onClick={() => navigate('/')}
              color="inherit"
              variant="outlined"
              size="small"
            >
              Exit
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 2 }}>
        {/* Enhanced Progress Overview */}
        <Card sx={{ mb: 2, background: 'white', boxShadow: 3 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  {companyContext?.parentCompany?.name || 'Company Name Not Set'}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                  🏭 {companyContext?.parentCompany?.industry || 'Industry Not Set'} • 
                  👥 {(companyContext?.parentCompany?.employeeCount || 0).toLocaleString()} employees
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  💰 Revenue: {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }).format(companyContext?.parentCompany?.revenue || 0)} • 
                  🎯 GCC Target: {companyContext?.gccCompany?.targetSize || 0} employees
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip 
                    label="Custom Calculation Config Active" 
                    size="small" 
                    color="primary"
                    variant="outlined"
                    onClick={() => setConfigDialogOpen(true)}
                    sx={{ cursor: 'pointer', mr: 1 }}
                  />
                  <Chip 
                    label={`${painPoints.length} Pain Points`} 
                    size="small" 
                    color="secondary"
                    variant="outlined"
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        Overall Progress
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="primary.main">
                        {progressionMetrics.completionPercentage}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={progressionMetrics.completionPercentage} 
                      sx={{ 
                        height: 12, 
                        borderRadius: 6,
                        background: 'linear-gradient(45deg, #e0e0e0 30%, #f5f5f5 90%)'
                      }}
                    />
                  </Box>
                  <Chip 
                    label={`Stage ${currentStage}`}
                    color="primary"
                    variant="filled"
                    sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}
                  />
                </Box>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, textAlign: 'center' }}>
                  <Box>
                    <Typography variant="h6" color="success.main" fontWeight="bold">
                      {progressionMetrics.completedCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Completed
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" color="warning.main" fontWeight="bold">
                      {inProgressSteps.size}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      In Progress
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" color="text.secondary" fontWeight="bold">
                      {progressionMetrics.remainingSteps}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Remaining
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Main Content Area */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Sidebar />
          </Grid>

          <Grid item xs={12} md={9}>
            <MainContent />
          </Grid>
        </Grid>
      </Container>

      {/* Quick Actions */}
      <QuickActions />

      {/* Value Leakage Analysis Dialog */}
      <ValueLeakageAnalysisDialog
        open={valueLeakageDialogOpen}
        onClose={() => setValueLeakageDialogOpen(false)}
        onComplete={(output, painPoints, metrics) => {
          console.log('Analysis completed with config:', calculationConfig);
          console.log('Generated output:', output);
          setValueLeakageDialogOpen(false);
        }}
        valueLeakageFramework={enhancedValueLeakageFramework}
        selectedLeakages={selectedLeakages}
        onLeakagesChange={setSelectedLeakages}
        priorities={priorities}
        onPrioritiesChange={setPriorities}
        companyContext={companyContext}
        calculationConfig={calculationConfig}
      />

      {/* Company Configuration Dialog */}
      <CompanyContextConfiguration
        open={companyConfigDialogOpen}
        onClose={() => setCompanyConfigDialogOpen(false)}
        onSave={(newContext) => {
          setCompanyContext(newContext);
          console.log('Company context updated:', newContext);
        }}
        currentContext={companyContext}
      />

      {/* Calculation Configuration Dialog */}
      <CalculationConfigurationPanel
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        onSave={(newConfig) => {
          setCalculationConfig(newConfig);
          console.log('Calculation configuration updated:', newConfig);
        }}
        currentConfig={calculationConfig}
      />

      {/* NEW: PDF Report Dialog */}
      <Dialog open={pdfDialogOpen} onClose={() => setPdfDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'center' }}>
          <PictureAsPdf sx={{ fontSize: 48, color: '#f44336', mb: 2 }} />
          <Typography variant="h5" component="div">
            Generate Transformation Report
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            This will generate a comprehensive PDF report including:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <Business color="primary" />
              </ListItemIcon>
              <ListItemText primary="Company Overview & Context" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Timeline color="primary" />
              </ListItemIcon>
              <ListItemText primary="Transformation Progress & Metrics" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Analytics color="primary" />
              </ListItemIcon>
              <ListItemText primary="Value Leakage Analysis Results" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <BarChart color="primary" />
              </ListItemIcon>
              <ListItemText primary="Pain Points & Priority Matrix" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Settings color="primary" />
              </ListItemIcon>
              <ListItemText primary="Calculation Configuration" />
            </ListItem>
          </List>
          <Alert severity="info" sx={{ mt: 2 }}>
            The report will use current company settings and calculation formulas.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button onClick={() => setPdfDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={generatePDFReport}
            variant="contained" 
            color="primary"
            startIcon={<Download />}
            size="large"
          >
            Generate PDF Report
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GCCTransformationTracker;